import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { BsUpcScan } from 'react-icons/bs';
import { FiPackage, FiAlertTriangle, FiClock, FiBarChart2, FiSettings, FiImage, FiSmartphone } from 'react-icons/fi';
import { MdLocalOffer, MdOutlineLabel } from 'react-icons/md';
import { TbTruckDelivery } from 'react-icons/tb';
import './Login.css';
import { authService } from '../services/AuthService';
import { toast } from 'react-toastify';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

const FEATURES = [
    {
        icon: TbTruckDelivery,
        title: 'Pedidos a proveedores',
        description: 'Gestioná compras, pagos y seguimiento de pedidos desde un solo lugar.',
    },
    {
        icon: BsUpcScan,
        title: 'Escaneo con el celular',
        description: 'Recorré el local escaneando códigos de barra para armar pedidos a proveedor.',
    },
    {
        icon: FiPackage,
        title: 'Control de stock',
        description: 'Manejá el inventario de productos con movimientos claros y trazabilidad.',
    },
    {
        icon: FiAlertTriangle,
        title: 'Alertas de stock bajo',
        description: 'Recibí avisos automáticos cuando un producto necesita reposición.',
    },
    {
        icon: MdLocalOffer,
        title: 'Promociones en caja',
        description: 'Configurá promociones que se aplican automáticamente en el pedido caja.',
    },
    {
        icon: MdOutlineLabel,
        title: 'Etiquetas de precio',
        description: 'Imprimí etiquetas con info dinámica y distintos tamaños según tu necesidad.',
    },
    {
        icon: FiClock,
        title: 'Avisos de vencimiento',
        description: 'Controlá fechas de vencimiento y actuá antes de perder mercadería.',
    },
    {
        icon: FiBarChart2,
        title: 'Reportes del negocio',
        description: 'Consultá reportes de ventas, productos y movimientos para analizar ingresos, egresos y el rendimiento de tu local.',
    },
    {
        icon: FiSmartphone,
        title: 'Carta digital',
        description: 'Ofrecé un catálogo online donde tus clientes pueden ver tus productos y hacer pedidos desde el celular.',
    },
    {
        icon: FiSettings,
        title: 'Configuraciones',
        description: 'Personalizá delivery, pagos, carta digital y más opciones según las necesidades de tu negocio.',
    },
    {
        icon: FiImage,
        title: 'Diseño de tu marca',
        description: 'Subí el logo de tu local y configurá banners para darle identidad visual a tu carta digital.',
    },
];

const FEATURE_INTERVAL_MS = 6000;
const FEATURE_TRANSITION_MS = 500;

const isFieldEmpty = (value) => value === undefined || value === null || String(value).trim() === '';

const getInputClassName = (value, hasError) => {
    const classes = ['login-input'];
    if (hasError) {
        classes.push('login-input--error');
    } else if (isFieldEmpty(value)) {
        classes.push('login-input--empty');
    } else {
        classes.push('login-input--filled');
    }
    return classes.join(' ');
};

const schema = yup.object().shape({
    identificationNumber: yup.string().required('El numero de local es requerido'),
    username: yup.string().required('El usuario es requerido'),
    password: yup.string().required('La contraseña es requerida')
});

export default function Login() {
    const navigate = useNavigate();
    const versionApp = "V5.2.0";
    const [isLoading, setIsLoading] = useState(false);
    const [loginAttempts, setLoginAttempts] = useState(0);
    const [isRateLimited, setIsRateLimited] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [activeFeature, setActiveFeature] = useState(0);
    const [featureAnim, setFeatureAnim] = useState('idle');
    const [isPaused, setIsPaused] = useState(false);
    const activeFeatureRef = useRef(0);
    const isTransitioningRef = useRef(false);

    const { register, handleSubmit, watch, formState: { errors } } = useForm({
        resolver: yupResolver(schema)
    });

    const identificationNumber = watch('identificationNumber');
    const username = watch('username');
    const password = watch('password');

    const changeFeature = useCallback((index) => {
        if (index === activeFeatureRef.current || isTransitioningRef.current) return;

        isTransitioningRef.current = true;
        setFeatureAnim('exit');

        setTimeout(() => {
            activeFeatureRef.current = index;
            setActiveFeature(index);
            setFeatureAnim('enter');

            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    setFeatureAnim('idle');
                    setTimeout(() => {
                        isTransitioningRef.current = false;
                    }, FEATURE_TRANSITION_MS);
                });
            });
        }, FEATURE_TRANSITION_MS);
    }, []);

    const goToFeature = useCallback((index) => {
        changeFeature(index);
    }, [changeFeature]);

    const nextFeature = useCallback(() => {
        const next = (activeFeatureRef.current + 1) % FEATURES.length;
        changeFeature(next);
    }, [changeFeature]);

    useEffect(() => {
        document.title = 'QReate - Login';
        const sessionExpiredMessage = sessionStorage.getItem('sessionExpiredMessage');
        if (sessionExpiredMessage) {
            sessionStorage.removeItem('sessionExpiredMessage');
            toast.warning(sessionExpiredMessage);
        }
        
        authService.isAuthenticated()
            .then(isAuth => {
                if (isAuth) {
                    navigate('/home');
                }
            })
            .catch(() => {
                // Backend no disponible o error de red: mostrar login sin romper la app
            });
    }, [navigate]);

    useEffect(() => {
        if (isPaused || featureAnim !== 'idle') return undefined;

        const timer = setTimeout(nextFeature, FEATURE_INTERVAL_MS);
        return () => clearTimeout(timer);
    }, [isPaused, nextFeature, activeFeature, featureAnim]);

    const onSubmit = async (data) => {
        if (isRateLimited) {
            toast.error('Demasiados intentos. Por favor, espere un momento');
            return;
        }

        setIsLoading(true);
        try {
            const featuresLoadedPromise = new Promise((resolve) => {
                const handleFeaturesLoaded = () => {
                    window.removeEventListener('featuresLoaded', handleFeaturesLoaded);
                    resolve();
                };
                window.addEventListener('featuresLoaded', handleFeaturesLoaded);

                setTimeout(() => {
                    window.removeEventListener('featuresLoaded', handleFeaturesLoaded);
                    resolve();
                }, 5000);
            });

            await authService.login(data.identificationNumber, data.username, data.password);
            await featuresLoadedPromise;
            navigate('/home');
        } catch (error) {
            setLoginAttempts(prev => prev + 1);
            if (loginAttempts >= 3) {
                setIsRateLimited(true);
                setTimeout(() => {
                    setIsRateLimited(false);
                    setLoginAttempts(0);
                }, 30000);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const currentFeature = FEATURES[activeFeature];
    const FeatureIcon = currentFeature.icon;

    return (
        <div className="login-page">
            <div className="login-layout">
                <aside
                    className="login-showcase"
                    onMouseEnter={() => setIsPaused(true)}
                    onMouseLeave={() => setIsPaused(false)}
                >
                    <div className="login-showcase-inner">
                        <p className="login-showcase-eyebrow">QReate</p>
                        <h2 className="login-showcase-title">
                            Todo lo que necesitás para gestionar tu negocio
                        </h2>

                        <div className={`login-feature-spotlight login-feature-spotlight--${featureAnim}`}>
                            <div className="login-feature-spotlight-icon">
                                <FeatureIcon />
                            </div>
                            <h3 className="login-feature-spotlight-title">{currentFeature.title}</h3>
                            <p className="login-feature-spotlight-desc">{currentFeature.description}</p>
                        </div>

                        <div className="login-feature-grid">
                            {FEATURES.map((feature, index) => {
                                const Icon = feature.icon;
                                return (
                                    <button
                                        key={feature.title}
                                        type="button"
                                        className={`login-feature-chip ${index === activeFeature ? 'login-feature-chip--active' : ''}`}
                                        onClick={() => goToFeature(index)}
                                        aria-label={feature.title}
                                        aria-pressed={index === activeFeature}
                                    >
                                        <Icon className="login-feature-chip-icon" />
                                        <span>{feature.title}</span>
                                    </button>
                                );
                            })}
                        </div>

                        <div className="login-feature-dots" role="tablist" aria-label="Funcionalidades">
                            {FEATURES.map((feature, index) => (
                                <button
                                    key={feature.title}
                                    type="button"
                                    role="tab"
                                    aria-selected={index === activeFeature}
                                    aria-label={feature.title}
                                    className={`login-feature-dot ${index === activeFeature ? 'login-feature-dot--active' : ''}`}
                                    onClick={() => goToFeature(index)}
                                />
                            ))}
                        </div>
                    </div>
                </aside>

                <main className="login-main">
                    <div className="login-container">
                        <div className="login-card">
                            <header className="login-header">
                                <img
                                    src={`${process.env.PUBLIC_URL}/logosolo.svg`}
                                    alt="QReate"
                                    className="login-logo"
                                />
                                <p className="login-tagline">Gestión integral para tu negocio</p>
                            </header>

                            <form className="login-form" onSubmit={handleSubmit(onSubmit)}>
                                <div className="login-field">
                                    <label
                                        htmlFor="identificationNumber"
                                        className={`login-label ${isFieldEmpty(identificationNumber) ? 'login-label--empty' : ''}`}
                                    >
                                        Número de local
                                    </label>
                                    <input
                                        id="identificationNumber"
                                        className={getInputClassName(identificationNumber, !!errors.identificationNumber)}
                                        type="text"
                                        inputMode="numeric"
                                        autoComplete="organization"
                                        {...register('identificationNumber')}
                                    />
                                    {errors.identificationNumber && (
                                        <p className="login-error">{errors.identificationNumber.message}</p>
                                    )}
                                </div>

                                <div className="login-field">
                                    <label
                                        htmlFor="username"
                                        className={`login-label ${isFieldEmpty(username) ? 'login-label--empty' : ''}`}
                                    >
                                        Usuario
                                    </label>
                                    <input
                                        id="username"
                                        className={getInputClassName(username, !!errors.username)}
                                        type="text"
                                        autoComplete="username"
                                        {...register('username')}
                                    />
                                    {errors.username && (
                                        <p className="login-error">{errors.username.message}</p>
                                    )}
                                </div>

                                <div className="login-field">
                                    <label
                                        htmlFor="password"
                                        className={`login-label ${isFieldEmpty(password) ? 'login-label--empty' : ''}`}
                                    >
                                        Contraseña
                                    </label>
                                    <div className="login-password-wrapper">
                                        <input
                                            id="password"
                                            className={`${getInputClassName(password, !!errors.password)} login-input--password`}
                                            type={showPassword ? 'text' : 'password'}
                                            autoComplete="current-password"
                                            {...register('password')}
                                        />
                                        <button
                                            type="button"
                                            className="login-password-toggle"
                                            onClick={() => setShowPassword(prev => !prev)}
                                            aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                                        >
                                            {showPassword ? <FaEyeSlash /> : <FaEye />}
                                        </button>
                                    </div>
                                    {errors.password && (
                                        <p className="login-error">{errors.password.message}</p>
                                    )}
                                </div>

                                <button
                                    type="submit"
                                    className="login-submit"
                                    disabled={isLoading || isRateLimited}
                                >
                                    {isLoading ? 'Ingresando...' : 'Ingresar'}
                                </button>
                            </form>

                            <footer className="login-version">{versionApp}</footer>
                        </div>
                    </div>
                </main>
            </div>
        </div>
    );
}
